import { snakeCase } from 'lodash'
import { isAreaArticleType, nullish, sizeCheckRequiredHeadingTypes } from '../ArticleParse'
import { Priority } from '../../common/types'
import type { Article } from '../types'

export const getArticleOccupyingLines = (article: Article): number => {
  if (isAreaArticleType(article.articleType)) {
    const area = article.areas?.[0]
    return (area?.width ?? 0) * (area?.height ?? 0)
  }
  const {
    headingNumberOfOccupyingRows,
    headingNumberOfOccupyingColumns,
    preambleMarginRows,
    preambleNumberOfColumns,
    bodyNumberOfRows,
    rows
  } = article
  const normalHeadingLines = (headingNumberOfOccupyingColumns ?? 0) * (headingNumberOfOccupyingRows ?? 0)
  const nonNormalHeadingLines =
    article?.headings
      ?.filter((h) => sizeCheckRequiredHeadingTypes.has(snakeCase(h.headingType)))
      ?.map((h) => (h.numberOfOccupyingRows ?? 0) * (h.numberOfOccupyingColumns ?? 0))
      ?.reduce((prev, curr) => prev + curr, 0) || 0
  const preambleLines = (preambleMarginRows ?? 0) * Number(preambleNumberOfColumns ?? 0)
  const figuresLines =
    article?.figures
      ?.map((fig) => {
        const { numberOfOccupyingRows, numberOfOccupyingColumns } = fig
        if (!nullish(numberOfOccupyingRows) && !nullish(numberOfOccupyingColumns)) {
          return Number(numberOfOccupyingRows ?? 0) * Number(numberOfOccupyingColumns ?? 0)
        }
        return 0
      })
      ?.reduce((prev, curr) => prev + curr, 0) || 0
  const bodyLines = (rows || bodyNumberOfRows) ?? 0
  const totalLines = normalHeadingLines + nonNormalHeadingLines + preambleLines + figuresLines + bodyLines
  return totalLines
}

export const getLargestOtherOrder = (articles: Article[]): number => {
  const otherArticles = articles
    .filter((a) => a.priority === Priority.Other)
    ?.sort((a, b) => (b.order ?? 0) - (a.order ?? 0))
  return otherArticles?.at(0)?.order ?? -1
}

export const getLargestNonAreaOtherOrder = (articles: Article[]): number => {
  const otherArticles = articles
    .filter((a) => a.priority === Priority.Other && !isAreaArticleType(a.articleType))
    ?.sort((a, b) => (b.order ?? 0) - (a.order ?? 0))
  return otherArticles?.at(0)?.order ?? -1
}

export const getSmallestNonAreaOtherOrder = (articles: Article[]): number => {
  const otherArticles = articles
    .filter((a) => a.priority === Priority.Other && !isAreaArticleType(a.articleType))
    ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  return otherArticles?.at(0)?.order ?? -1
}

export const childArticleInfo = (article: Article, parentGroupMap: Map<string | null | undefined, Article[]>) => {
  const childGroup = !nullish(article?.parentId) ? (parentGroupMap.get(article?.parentId) ?? []) : []
  const posWithinChildGroup = childGroup.findIndex((a) => a.id === article.id)
  const isSingleChild = childGroup.length === 1
  const isFirstElementChildGroup = childGroup.length > 1 && posWithinChildGroup === 0
  const isLastElementChildGroup = childGroup.length > 1 && posWithinChildGroup === childGroup.length - 1
  return { isSingleChild, isFirstElementChildGroup, isLastElementChildGroup }
}

export const getSwitchUpTargetIdx = (article: Article, rowIdx: number, parentArticleIndices: number[]) => {
  if (!nullish(article?.parentId)) {
    return rowIdx - 1
  }
  const posParentArticleIndices = parentArticleIndices.findIndex((i) => i === rowIdx)
  return parentArticleIndices[posParentArticleIndices - 1]
}

export const getSwitchDownTargetIdx = (article: Article, rowIdx: number, parentArticleIndices: number[]) => {
  if (!nullish(article?.parentId)) {
    return rowIdx + 1
  }
  const posParentArticleIndices = parentArticleIndices.findIndex((i) => i === rowIdx)
  return parentArticleIndices[posParentArticleIndices + 1]
}
